/**
 * Footer Component
 *
 * Application footer with links and copyright information.
 *
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../contexts/BrandContext';
import { useConsent } from '../../contexts/useConsent';
import { cn } from '../../utils/cn';

export interface FooterProps {
  className?: string;
  showLinks?: boolean;
  customLinks?: Array<{
    label: string;
    href: string;
  }>;
}

export const Footer: React.FC<FooterProps> = ({ className }) => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { bannerEnabled, openSettings } = useConsent();

  return (
    <footer
      className={cn(
        'bg-white dark:bg-gray-800 brand-footer-bg border-t border-gray-200 dark:border-gray-700',
        'py-3 px-4 md:py-4 md:px-6',
        className
      )}
    >
      <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
        {/* Links */}
        <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6 text-xs md:text-sm">
          <a
            href="https://novofon.ai/impressum/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            {t('footer.imprint')}
          </a>
          <a
            href="https://novofon.ai/datenschutz/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            {t('footer.privacy')}
          </a>
          <a
            href="https://novofon.ai/allgemeine-geschaeftsbedingungen-agb/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            {t('footer.terms')}
          </a>
          {bannerEnabled && (
            <button
              type="button"
              onClick={openSettings}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
            >
              {t('cookieConsent.footerLink')}
            </button>
          )}
        </div>

        {/* Copyright */}
        <div className="text-xs md:text-sm font-normal leading-27 text-gray-600 dark:text-gray-400 text-center md:text-right">
          {brand.footer_text || t('common.copyright')}
        </div>
      </div>
    </footer>
  );
};
